import ApiWithLogging from '../utils/apiWithLogging';
import codeGeneratorService from './codeGeneratorService';

// สถานะสัญญา - ตรงกับ Backend enum values
export enum ContractStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE', 
  EXPIRED = 'EXPIRED',
  TERMINATED = 'TERMINATED'
}

// สถานะเอกสาร - ตรงกับ Backend enum values
export enum DocumentStatus {
  PENDING = 'PENDING',
  DRAFT = 'DRAFT',
  GENERATED = 'GENERATED',
  SENT = 'SENT',
  CUSTOMER_SIGNED = 'CUSTOMER_SIGNED',
  COMPLETED = 'COMPLETED'
}

// ลบ ContractType enum ออกแล้ว

// FB-2026-0004/0006: ประเภทสัญญาแบบใหม่ (null = สัญญาแบบเดิม)
export type ContractKind = 'MAIN' | 'ADDENDUM';
export type DepositTemplate = 'WITH_DEPOSIT' | 'ACCUMULATED_WAIVER';

export const DEPOSIT_TEMPLATE_LABEL: Record<DepositTemplate, string> = {
  WITH_DEPOSIT: '02 · มีเงินมัดจำ',
  ACCUMULATED_WAIVER: '03 · ไม่วางมัดจำ (มัดจำสะสมเกินเกณฑ์)',
};

/** ข้อความประเภทสัญญาสำหรับแสดงผล */
export const contractKindLabel = (c: Pick<Contract, 'contract_type' | 'master_contract_number' | 'master_contract_id'>): string => {
  if (c.contract_type === 'MAIN') return 'สัญญาหลัก';
  if (c.contract_type === 'ADDENDUM') {
    return c.master_contract_number ? `แนบท้าย ของ ${c.master_contract_number}` : 'สัญญาแนบท้าย';
  }
  return c.master_contract_id ? 'สัญญาลูก (แบบเดิม)' : 'สัญญาแบบเดิม';
};

/** สัญญาไม่มีวันสิ้นสุด (สัญญาแบบเปิด) */
export const isOpenEndedContract = (c: Pick<Contract, 'end_date' | 'is_open_ended'>): boolean =>
  Boolean(c.is_open_ended) || !c.end_date;

export interface AddendumCandidate {
  rental_id: number;
  rental_number: string;
  rental_item_id?: number | null;
  item_count?: number | null;
  site_id?: number | null;
  customer_name?: string | null;
  product_id?: number | null;
  program_name?: string | null;
  device_id?: number | null;
  device_serial?: string | null;
  device_brand?: string | null;
  device_model?: string | null;
  rental_rate?: number | null;
  deposit_amount?: number | null;
  start_date?: string | null;
  first_payment_date?: string | null;
  suggested_template: DepositTemplate;
  min_term_months?: number | null;
}

export interface AddendumCandidatesResponse {
  master_contract_id: number;
  master_contract_number: string;
  master_contract_date?: string | null;
  company_id: number;
  company_name?: string | null;
  deposit_held_total: number;
  deposit_waiver_threshold: number;
  items: AddendumCandidate[];
}

export interface AddendumPayload {
  rental_id?: number;
  rental_item_id?: number | null;
  contract_number?: string | null;
  contract_date?: string | null;
  start_date?: string;
  first_payment_date?: string | null;
  rental_rate?: number;
  deposit_amount?: number;
  deposit_template?: DepositTemplate | null;
  device_brand?: string | null;
  device_model?: string | null;
  device_serial?: string | null;
  program_name?: string | null;
  customer_name?: string | null;
  min_term_months?: number;
  notes?: string | null;
}

export interface MainContractCreate {
  contract_number?: string | null;
  company_id: number;
  contract_date: string;
  signer_ids?: number[];
  terms_conditions?: string;
  notes?: string;
}

export interface ContractDocumentSettings {
  lessor_representative: string;
  lessor_representative_position: string;
  lessor_witness: string;
  bank_name: string;
  bank_account_name: string;
  bank_account_number: string;
  payment_day: number;
  min_term_months: number;
  deposit_waiver_threshold: number;
}

// ข้อมูลสัญญา (ตรงกับ backend schema)
export interface Contract {
  id: number;
  contract_number: string;
  master_contract_id?: number;
  /** เลขที่สัญญาหลัก (backend คำนวณให้ · สัญญาลูกเท่านั้น) */
  master_contract_number?: string | null;
  company_id: number;
  site_id?: number;
  start_date: string;
  /** FB-2026-0004: สัญญาหลัก/แนบท้าย = สัญญาแบบเปิด → null เสมอ */
  end_date: string | null;
  contract_value: number;
  payment_term: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  status: ContractStatus;
  /** 'MAIN' = สัญญาหลัก · 'ADDENDUM' = สัญญาแนบท้าย (บันทึกส่ง-มอบ) · null = สัญญาแบบเดิม (legacy)
   *  (string ไว้รองรับค่าที่รายงานเก่าใช้ เช่น 'master'/'sub') */
  contract_type?: ContractKind | string | null;
  /** วันที่ทำสัญญา (YYYY-MM-DD) */
  contract_date?: string | null;
  /** แบบเอกสารแนบท้าย: 02 มีเงินมัดจำ / 03 ไม่วางมัดจำ (มัดจำสะสมเกินเกณฑ์) */
  deposit_template?: DepositTemplate | null;
  is_open_ended?: boolean;
  /** MAIN: จำนวนแนบท้ายที่ยังไม่ถูกยกเลิก */
  addendum_count?: number | null;
  flow_account_contact_id?: string;
  notes?: string;
  terms_and_conditions?: string;
  terms_conditions?: string; // alias (legacy snake_case variant)
  document_version?: string;
  document_url?: string;
  signed_by_lessor?: string;
  lessor_signed_date?: string;
  signed_by_lessee?: string;
  lessee_signed_date?: string;
  customer_signed_at?: string; // alias of lessee_signed_date (legacy view)
  document_status?: string;    // เอกสารสถานะ (เพิ่มเมื่อ BE ส่งมา)
  created_by_user_id: number;
  created_by_name?: string;
  approved_by_user_id?: number;
  created_at: string;
  updated_at?: string;
  
  // Relations (จะมีเมื่อ include ใน API)
  company?: {
    id: number;
    name: string;
    code: string;
  };
  company_name?: string;
  site?: {
    id: number;
    name: string;
    code: string;
    contactPerson?: string;
    contact_name?: string;
    phone?: string;
    contact_phone?: string;
    email?: string;
    contact_email?: string;
    address?: string;
  };
  site_name?: string;
  master_contract?: Contract;
  sub_contracts?: Contract[];
  contract_items?: ContractItem[];
  contract_signers?: ContractSignerSnapshot[];
}

// ข้อมูล snapshot ผู้ลงนามในสัญญา
export interface ContractSignerSnapshot {
  id: number;
  contract_id: number;
  authorized_signer_id: number;
  signer_full_name: string;
  signer_position: string;
  sort_order: number;
  created_at: string;
}

// รายการอุปกรณ์ในสัญญา
export interface ContractItem {
  id: number;
  contract_id: number;
  rental_id: number;
  product_id: number;
  device_id?: number;
  device_serial?: string;
  rental_rate: number;
  rental_period_months: number;
  quantity: number;
  item_start_date: string;
  item_end_date: string | null;
  notes?: string;
  created_at: string;
  // FB-2026-0006: ข้อมูลบันทึกส่ง-มอบ (สัญญาแนบท้าย)
  rental_item_id?: number | null;
  rental_number?: string | null;
  deposit_amount?: number | null;
  first_payment_date?: string | null;
  device_brand?: string | null;
  device_model?: string | null;
  program_name?: string | null;
  customer_name?: string | null;
  updated_at?: string;
  
  // Relations
  rental?: {
    id: number;
    rental_number: string;
  };
  product?: {
    id: number;
    name: string;
    code: string;
  };
  device?: {
    id: number;
    serial_number: string;
  };
}

// สำหรับสร้างสัญญาจากการเช่า
export interface ContractGenerationRequest {
  rental_ids: number[];
  master_contract_id?: number;
}

export interface ContractGenerationResponse {
  master_contract?: Contract;
  sub_contracts: Contract[];
  contract_items: ContractItem[];
  message: string;
}

// ฟอร์มสร้าง/แก้ไขสัญญา
export interface ContractForm {
  contract_number: string;
  master_contract_id?: number;
  company_id: number;
  site_id?: number;
  start_date?: string;
  end_date?: string | null;
  contract_date?: string | null;
  contract_value?: number; // เปลี่ยนเป็น optional
  payment_term?: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  status?: ContractStatus;
  flow_account_contact_id?: string;
  notes?: string;
  terms_and_conditions?: string;
  terms_conditions?: string; // alias (legacy snake_case variant)
  document_version?: string;
  rental_ids?: number[];
  signer_ids?: number[];
  amendment_reason?: string;
}

// สำหรับสร้างสัญญาแบบง่าย (ไม่แบ่ง Master/Sub)
export interface ContractSimpleCreate {
  contract_number: string;
  company_id: number;
  start_date: string;
  end_date: string;
  rental_ids: number[];
  terms_conditions?: string;
  notes?: string;
  signer_ids?: number[];
}

export interface ContractSimpleResponse {
  contract: Contract;
  updated_rentals: number;
  message: string;
}

// สำหรับ Document Tracking
export interface DocumentTrackingItem {
  id: number;
  contract_id: number;  // เพิ่ม contract_id
  contract_number: string;
  company_name: string;
  site_name?: string;
  document_status: DocumentStatus;
  sent_to_customer_at?: string;
  customer_signed_at?: string;
  completed_at?: string;
  days_in_current_status: number;
}

// สำหรับ response ที่แปลงแล้วสำหรับ frontend
export interface DocumentTrackingItemForFrontend extends DocumentTrackingItem {
  company?: { name: string } | null;
  site?: { name: string } | null;
  master_contract_id?: number;
  contract_type?: string;
  master_contract_number?: string;
  rental_number?: string;
  /** สถานะสัญญา (backend ส่งมา) — ใช้ซ่อนปุ่มเดินเอกสารเมื่อสัญญาสิ้นสุดแล้ว */
  contract_status?: string | null;
}

export interface DocumentTrackingResponse {
  items: DocumentTrackingItemForFrontend[];
  total: number;
  page: number;
  limit: number;
  // Aggregate stats per document_status จาก backend (นับจาก query ที่ filter แล้วทั้งชุด ไม่ใช่เฉพาะหน้าปัจจุบัน)
  stats?: { [key: string]: number };
}

// พารามิเตอร์สำหรับดึงข้อมูลสัญญา
export interface GetContractsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: ContractStatus;
  company_id?: number;
  site_id?: number;
  start_date?: string;
  end_date?: string;
  from_date?: string; // alias for date-range filter
  to_date?: string;
  is_expiring_soon?: boolean;
  days_to_expiry?: number;
  /** เฉพาะสัญญาระดับบนสุด (ไม่มีสัญญาแม่) — ตัดแนบท้าย/สัญญาลูกออก (หน้าสร้างสัญญาหลักใช้นับเตือนซ้ำ) */
  top_level_only?: boolean;
}

export interface ContractListResponse {
  items: Contract[];
  total: number;
  page: number;
  limit: number;
}

// บริการจัดการสัญญา
const contractService = {
  // ดึงรายการสัญญาทั้งหมด
  async getContracts(params: GetContractsParams = {}): Promise<ContractListResponse> {
    const queryParams = {
      page: params.page || 1,
      limit: Math.min(params.limit || 10, 50), // จำกัดไม่เกิน 50 รายการ
      ...params
    };
    
    try {
      const response = await ApiWithLogging.get<ContractListResponse>('/contracts/', 
        { params: queryParams },
        {
          service: 'contractService',
          action: 'view_contracts_list',
          entityType: 'contract'
        }
      );
      
      return response;
    } catch (error: any) {
      // หาก limit สูงเกินไปให้ลองด้วย limit เล็กลง
      if (error.status === 500 && queryParams.limit > 10) {
        console.warn('Retrying with smaller limit due to server error');
        return this.getContracts({
          ...params,
          limit: 10
        });
      }
      throw error;
    }
  },

  // ดึงสัญญาหลักทั้งหมด
  async getMasterContracts(params: GetContractsParams = {}): Promise<ContractListResponse> {
    const queryParams = {
      page: params.page || 1,
      limit: params.limit || 10,
      ...params
    };
    
    const response = await ApiWithLogging.get<ContractListResponse>('/contracts/master', 
      { params: queryParams },
      {
        service: 'contractService',
        action: 'view_master_contracts',
        entityType: 'contract'
      }
    );
    
    return response;
  },

  // ดึงสัญญาลูกของสัญญาหลัก
  async getSubContracts(masterContractId: number): Promise<Contract[]> {
    const response = await ApiWithLogging.get<Contract[]>(`/contracts/${masterContractId}/sub-contracts`, {},
      {
        service: 'contractService',
        action: 'view_sub_contracts',
        entityType: 'contract',
        entityId: masterContractId
      }
    );
    return response;
  },

  // ดึงรายการอุปกรณ์ในสัญญา
  async getContractItems(contractId: number): Promise<ContractItem[]> {
    const response = await ApiWithLogging.get<ContractItem[]>(`/contracts/${contractId}/items`, {},
      {
        service: 'contractService',
        action: 'view_contract_items',
        entityType: 'contract',
        entityId: contractId
      }
    );
    return response;
  },

  // สร้างสัญญาจากการเช่า
  async generateContracts(request: ContractGenerationRequest): Promise<ContractGenerationResponse> {
    const response = await ApiWithLogging.post<ContractGenerationResponse>('/contracts/generate', request, {},
      {
        service: 'contractService',
        action: 'generate_contracts',
        entityType: 'contract',
        details: {
          rental_ids: request.rental_ids
        }
      }
    );
    return response;
  },

  // สร้างเลขที่สัญญาใหม่
  async generateContractNumber(contractType: 'master' | 'sub' = 'master'): Promise<{contract_number: string; contract_type: string}> {
    const response = await ApiWithLogging.post<{contract_number: string; contract_type: string}>('/contracts/generate-number', 
      null, 
      { params: { contract_type: contractType }},
      {
        service: 'contractService',
        action: 'generate_contract_number',
        entityType: 'contract',
        details: { contract_type: contractType }
      }
    );
    return response;
  },

  // ดึงสัญญาหลักของบริษัท (สำหรับเลือกเป็น master contract)
  async getMasterContractsByCompany(companyId: number): Promise<ContractListResponse> {
    const response = await ApiWithLogging.get<ContractListResponse>(`/contracts/master-contracts/${companyId}`, {},
      {
        service: 'contractService',
        action: 'view_company_master_contracts',
        entityType: 'contract',
        details: { company_id: companyId }
      }
    );
    return response;
  },

  // ดึงสัญญาตาม ID
  async getContract(id: number): Promise<Contract> {
    const response = await ApiWithLogging.get<Contract>(`/contracts/${id}`, {},
      {
        service: 'contractService',
        action: 'view_contract_detail',
        entityType: 'contract',
        entityId: id
      }
    );
    return response;
  },

  // สร้างสัญญาใหม่
  async createContract(data: ContractForm): Promise<Contract> {
    const response = await ApiWithLogging.post<Contract>('/contracts/', data, {},
      {
        service: 'contractService',
        action: 'create_contract',
        entityType: 'contract',
        details: {
          contract_number: data.contract_number,
          company_id: data.company_id
        }
      }
    );
    return response;
  },

  // แก้ไขสัญญา
  // S1228: เพิ่มพารามิเตอร์ ifMatch (optional) — pass contract.updated_at จาก initial read
  // เพื่อ enable optimistic-concurrency check (BE จะ return 412 ถ้า stale)
  // ถ้าไม่ส่ง — ทำงานเหมือนเดิม (LWW backward-compatible)
  async updateContract(id: number, data: Partial<ContractForm>, ifMatch?: string): Promise<Contract> {
    const config = ifMatch ? { headers: { 'If-Match': ifMatch } } : {};
    const response = await ApiWithLogging.put<Contract>(`/contracts/${id}`, data, config,
      {
        service: 'contractService',
        action: 'update_contract',
        entityType: 'contract',
        entityId: id,
        details: {
          updated_fields: Object.keys(data),
          status: data.status,
          if_match_used: Boolean(ifMatch),
        }
      }
    );
    return response;
  },

  // อนุมัติสัญญา
  async approveContract(id: number, approved: boolean, notes?: string): Promise<Contract> {
    const response = await ApiWithLogging.post<Contract>(`/contracts/${id}/approve`, 
      { approved, notes }, {},
      {
        service: 'contractService',
        action: approved ? 'approve_contract' : 'reject_contract',
        entityType: 'contract',
        entityId: id,
        details: { approved, notes }
      }
    );
    return response;
  },

  // ยกเลิกสัญญา
  async terminateContract(id: number): Promise<Contract> {
    const response = await ApiWithLogging.post<Contract>(`/contracts/${id}/terminate`, {}, {},
      {
        service: 'contractService',
        action: 'terminate_contract',
        entityType: 'contract',
        entityId: id
      }
    );
    return response;
  },

  // ยกเลิกสัญญา (alias สำหรับ terminateContract)
  async cancelContract(id: number): Promise<Contract> {
    return this.terminateContract(id);
  },

  // ต่อสัญญา (สร้างสัญญาใหม่จากสัญญาเดิม)
  async renewContract(id: number): Promise<{
    success: boolean;
    message: string;
    new_contract_id: number;
    new_contract_number: string;
    new_start_date: string;
    new_end_date: string;
  }> {
    const response = await ApiWithLogging.post(`/contracts/${id}/renew`, {}, {},
      {
        service: 'contractService',
        action: 'renew_contract',
        entityType: 'contract',
        entityId: id
      }
    );
    return response;
  },

  // ลบสัญญา
  async deleteContract(id: number): Promise<void> {
    await ApiWithLogging.delete(`/contracts/${id}`, {},
      {
        service: 'contractService',
        action: 'delete_contract',
        entityType: 'contract',
        entityId: id
      }
    );
  },

  // ดาวน์โหลด PDF สัญญา (รองรับ options สำหรับสัญญาลูก)
  async downloadContractPDF(id: number, options?: { rental_id?: number; include_addenda?: boolean }): Promise<Blob> {
    // สร้าง query params ถ้ามี rental_id / include_addenda (สัญญาหลัก + แนบท้ายทั้งหมด)
    const params: Record<string, unknown> = {};
    if (options?.rental_id) params.rental_id = options.rental_id;
    if (options?.include_addenda) params.include_addenda = true;
    
    const response = await ApiWithLogging.get(`/contracts/${id}/pdf`, 
      { 
        responseType: 'blob',
        params: Object.keys(params).length ? params : undefined
      },
      {
        service: 'contractService',
        action: options?.rental_id ? 'download_sub_contract_pdf' : 'download_contract_pdf',
        entityType: 'contract',
        entityId: id,
        details: options
      }
    );
    return response;
  },

  // ดาวน์โหลด PDF สัญญาและบันทึกเป็นไฟล์
  async downloadAndSaveContractPDF(contract: Contract, options?: { include_addenda?: boolean }): Promise<void> {
    try {
      const blob = await this.downloadContractPDF(contract.id, options);
      
      // สร้างชื่อไฟล์
      const suffix = options?.include_addenda ? '_with_addenda' : '';
      const fileName = `contract_${contract.contract_number.replace(/\//g, '_')}${suffix}.pdf`;
      
      // สร้าง URL สำหรับ download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading contract PDF:', error);
      throw error;
    }
  },

  // ดึงสัญญาตามบริษัท
  async getContractsByCompany(companyId: number, params: GetContractsParams = {}): Promise<ContractListResponse> {
    const queryParams = {
      page: params.page || 1,
      limit: params.limit || 10,
      ...params
    };
    
    const response = await ApiWithLogging.get<ContractListResponse>(`/contracts/company/${companyId}`, 
      { params: queryParams },
      {
        service: 'contractService',
        action: 'view_contracts_by_company',
        entityType: 'contract',
        details: { company_id: companyId }
      }
    );
    
    return response;
  },

  // ดึงสัญญาตามสถานะ
  async getContractsByStatus(status: ContractStatus, params: GetContractsParams = {}): Promise<ContractListResponse> {
    const queryParams = {
      page: params.page || 1,
      limit: params.limit || 10,
      ...params
    };
    
    const response = await ApiWithLogging.get<ContractListResponse>(`/contracts/status/${status}`, 
      { params: queryParams },
      {
        service: 'contractService',
        action: 'view_contracts_by_status',
        entityType: 'contract',
        details: { status }
      }
    );
    
    return response;
  },

  // ดึงสัญญาที่ใกล้หมดอายุ
  async getExpiringContracts(days: number = 30, params: GetContractsParams = {}): Promise<ContractListResponse> {
    const queryParams = {
      days,
      page: params.page || 1,
      limit: params.limit || 10,
      ...params
    };
    
    const response = await ApiWithLogging.get<ContractListResponse>('/contracts/expiring', 
      { params: queryParams },
      {
        service: 'contractService',
        action: 'view_expiring_contracts',
        entityType: 'contract',
        details: { days }
      }
    );
    
    return response;
  },

  // ดึงสรุปข้อมูลสัญญา
  async getContractSummary(): Promise<{
    total_contracts: number;
    active_contracts: number;
    expired_contracts: number;
    total_value: number;
    monthly_revenue: number;
  }> {
    const response = await ApiWithLogging.get('/contracts/summary', {},
      {
        service: 'contractService',
        action: 'view_contract_summary',
        entityType: 'contract'
      }
    );
    return response;
  },

  // ดึงเอกสารของสัญญา
  async getContractDocuments(contractId: number): Promise<any[]> {
    const response = await ApiWithLogging.get<any[]>(`/contracts/${contractId}/documents`, {},
      {
        service: 'contractService',
        action: 'view_contract_documents',
        entityType: 'contract',
        entityId: contractId
      }
    );
    return response;
  },

  // ดาวน์โหลดเอกสาร
  async downloadContractDocument(documentId: number): Promise<Blob> {
    const response = await ApiWithLogging.get(`/documents/${documentId}/download`, 
      { responseType: 'blob' },
      {
        service: 'contractService',
        action: 'download_contract_document',
        entityType: 'document',
        entityId: documentId
      }
    );
    return response;
  },

  // อัพโหลดเอกสารสัญญา
  async uploadContractDocument(contractId: number, file: File): Promise<{message: string; document_url: string}> {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      console.log(`Uploading document for contract ${contractId}:`, file.name, file.size);
      
      const response = await ApiWithLogging.post(`/contracts/${contractId}/document`, formData, 
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
        {
          service: 'contractService',
          action: 'upload_contract_document',
          entityType: 'contract',
          entityId: contractId,
          details: {
            filename: file.name,
            fileSize: file.size
          }
        }
      );
      
      console.log('Upload response:', response.data);
      return response;
    } catch (error: any) {
      console.error('Error in uploadContractDocument:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  },

  // สร้างสัญญาแบบง่าย (ไม่แบ่ง Master/Sub)
  async createSimpleContract(data: ContractSimpleCreate): Promise<ContractSimpleResponse> {
    const response = await ApiWithLogging.post<ContractSimpleResponse>('/contracts/simple', data, {},
      {
        service: 'contractService',
        action: 'create_simple_contract',
        entityType: 'contract',
        details: {
          contract_number: data.contract_number,
          company_id: data.company_id,
          rental_ids: data.rental_ids
        }
      }
    );
    return response;
  },

  // เพิ่มสัญญาลูกให้กับสัญญาหลัก
  async addSubContractToMaster(masterContractId: number, data: { rental_ids: number[], notes?: string | null }): Promise<Contract> {
    const response = await ApiWithLogging.post<Contract>(`/contracts/${masterContractId}/add-sub`, data, {},
      {
        service: 'contractService',
        action: 'add_sub_contract',
        entityType: 'contract',
        entityId: masterContractId,
        details: {
          rental_ids: data.rental_ids,
          master_contract_id: masterContractId
        }
      }
    );
    return response;
  },

  // === FB-2026-0004/0006: สัญญาหลัก + สัญญาแนบท้าย (บันทึกส่ง-มอบ) ===

  // สร้างสัญญาหลัก (ไม่มีวันเริ่ม/วันสิ้นสุด — สัญญาแบบเปิด)
  async createMainContract(data: MainContractCreate): Promise<Contract> {
    return ApiWithLogging.post<Contract>('/contracts/main', data, {},
      {
        service: 'contractService',
        action: 'create_main_contract',
        entityType: 'contract',
        details: { contract_number: data.contract_number, company_id: data.company_id }
      }
    );
  },

  // รายการเช่าของบริษัทที่ยังไม่มีสัญญาแนบท้าย (พร้อมค่าที่ดึงมาเติมฟอร์ม)
  async getAddendumCandidates(mainContractId: number, search?: string): Promise<AddendumCandidatesResponse> {
    return ApiWithLogging.get<AddendumCandidatesResponse>(`/contracts/${mainContractId}/addendum-candidates`,
      { params: search ? { search } : undefined },
      {
        service: 'contractService',
        action: 'view_addendum_candidates',
        entityType: 'contract',
        entityId: mainContractId
      }
    );
  },

  // เพิ่มสัญญาแนบท้าย (1 ฉบับ = 1 รายการเช่า)
  async createAddendum(mainContractId: number, data: AddendumPayload): Promise<Contract> {
    return ApiWithLogging.post<Contract>(`/contracts/${mainContractId}/addenda`, data, {},
      {
        service: 'contractService',
        action: 'create_addendum',
        entityType: 'contract',
        entityId: mainContractId,
        details: { rental_id: data.rental_id, deposit_template: data.deposit_template }
      }
    );
  },

  // แก้ไขสัญญาแนบท้าย (เฉพาะสถานะร่าง)
  async updateAddendum(addendumId: number, data: AddendumPayload): Promise<Contract> {
    const { rental_id: _r, rental_item_id: _ri, ...body } = data;
    return ApiWithLogging.put<Contract>(`/contracts/${addendumId}/addendum`, body, {},
      {
        service: 'contractService',
        action: 'update_addendum',
        entityType: 'contract',
        entityId: addendumId,
        details: { updated_fields: Object.keys(body) }
      }
    );
  },

  // ตั้งค่าข้อมูลผู้ให้เช่าในเอกสารสัญญา
  async getDocumentSettings(): Promise<ContractDocumentSettings> {
    return ApiWithLogging.get<ContractDocumentSettings>('/contracts/document-settings', {},
      { service: 'contractService', action: 'view_contract_document_settings', entityType: 'contract' }
    );
  },

  async updateDocumentSettings(data: Partial<ContractDocumentSettings>): Promise<ContractDocumentSettings> {
    return ApiWithLogging.put<ContractDocumentSettings>('/contracts/document-settings', data, {},
      { service: 'contractService', action: 'update_contract_document_settings', entityType: 'contract' }
    );
  },

  // === Document Tracking Methods ===
  
  // ดึงรายการสัญญาพร้อมสถานะเอกสาร
  async getDocumentTracking(params: {
    page?: number;
    limit?: number;
    document_status?: DocumentStatus | string;
    company_id?: number;
    site_id?: number;
    contract_id?: number;
    search?: string;
  } = {}): Promise<DocumentTrackingResponse> {
    // ApiWithLogging.get already returns the data, not the full response
    const backendResponse = await ApiWithLogging.get<{ items: DocumentTrackingItem[]; total: number; stats?: { [key: string]: number } }>('/contracts/document/tracking',
      { params },
      {
        service: 'contractService',
        action: 'view_document_tracking',
        entityType: 'contract'
      }
    );
    
    // Log response for debugging
    console.log('Document tracking API response:', backendResponse);
    console.log('Access token exists:', !!localStorage.getItem('access_token'));
    
    // Check if response exists and has items
    if (!backendResponse || !backendResponse.items || !Array.isArray(backendResponse.items)) {
      console.error('Invalid response from document tracking API:', backendResponse);
      return {
        items: [],
        total: backendResponse?.total || 0,
        page: params.page || 1,
        limit: params.limit || 20
      };
    }
    
    return {
      items: backendResponse.items.map((item: DocumentTrackingItem) => ({
        ...item,
        // Map backend fields to frontend interface expectations with additional properties
        company: item.company_name ? { name: item.company_name } : null,
        site: item.site_name ? { name: item.site_name } : null,
        days_in_current_status: item.days_in_current_status || 0,
        // Include master_contract_id if it exists in the backend response
        master_contract_id: (item as any).master_contract_id || undefined,
        // Include contract_type and master_contract_number from backend
        contract_type: (item as any).contract_type || undefined,
        master_contract_number: (item as any).master_contract_number || undefined
      })) as DocumentTrackingItemForFrontend[],
      total: backendResponse.total,
      page: params.page || 1,
      limit: params.limit || 20,
      stats: backendResponse.stats
    };
  },

  // สร้างเอกสาร PDF
  async generateDocument(contractId: number, trackingId?: number, data?: { notes?: string }): Promise<any> {
    // ส่ง tracking_id เป็น query parameter ถ้ามี
    const params = trackingId ? { tracking_id: trackingId } : {};
    
    // BUG-S2085-003: ส่งหมายเหตุจาก modal ไปด้วย (เดิมส่ง {} → หมายเหตุที่พิมพ์หายเงียบ)
    const response = await ApiWithLogging.post(`/contracts/${contractId}/document/generate`, { notes: data?.notes || undefined }, 
      { params },
      {
        service: 'contractService',
        action: 'generate_document',
        entityType: 'contract',
        entityId: contractId,
        details: { trackingId }
      }
    );
    
    return response;
  },

  // บันทึกการส่งเอกสาร
  async sendDocument(contractId: number, data: { email: string; notes?: string }, trackingId?: number): Promise<any> {
    const params = trackingId ? { tracking_id: trackingId } : {};
    
    const response = await ApiWithLogging.post(`/contracts/${contractId}/document/send`, data, 
      { params },
      {
        service: 'contractService',
        action: 'send_document',
        entityType: 'contract',
        entityId: contractId,
        details: { email: data.email, trackingId }
      }
    );
    return response;
  },

  // บันทึกการเซ็นเอกสาร
  async markDocumentSigned(contractId: number, data: { signed_by: string; notes?: string }, trackingId?: number): Promise<any> {
    const params = trackingId ? { tracking_id: trackingId } : {};
    
    const response = await ApiWithLogging.post(`/contracts/${contractId}/document/sign`, data, 
      { params },
      {
        service: 'contractService',
        action: 'sign_document',
        entityType: 'contract',
        entityId: contractId,
        details: { signed_by: data.signed_by, trackingId }
      }
    );
    return response;
  },

  // บันทึกว่าเอกสารเสร็จสมบูรณ์
  async completeDocument(contractId: number, data: { notes?: string }, trackingId?: number): Promise<any> {
    const params = trackingId ? { tracking_id: trackingId } : {};
    
    const response = await ApiWithLogging.post(`/contracts/${contractId}/document/complete`, data, 
      { params },
      {
        service: 'contractService',
        action: 'complete_document',
        entityType: 'contract',
        entityId: contractId,
        details: { trackingId }
      }
    );
    return response;
  },

  // ดูประวัติการกระทำกับเอกสาร
  async getDocumentLogs(contractId: number): Promise<any[]> {
    const response = await ApiWithLogging.get(`/contracts/${contractId}/document/logs`, {},
      {
        service: 'contractService',
        action: 'view_document_logs',
        entityType: 'contract',
        entityId: contractId
      }
    );
    return response;
  },

  // ตรวจสอบว่าเลขที่สัญญาซ้ำหรือไม่
  async checkCodeExists(contractNumber: string): Promise<boolean> {
    try {
      const response = await ApiWithLogging.get<{ exists: boolean }>(
        `/contracts/check-code/${encodeURIComponent(contractNumber)}`
      );
      return response.exists;
    } catch (error) {
      console.error('Error checking contract number:', error);
      return false;
    }
  }
};

// Named exports สำหรับ React Query hooks
export const getContracts = contractService.getContracts;
export const getContract = contractService.getContract;
export const createContract = contractService.createContract;
export const updateContract = contractService.updateContract;
export const deleteContract = contractService.deleteContract;

export default contractService;